import {
  postCreateUser,
  postMeRegisterPalms,
  postReregisterUserPalms,
  postUserAuthenticate,
  postUserSignin,
  postVerifyCurrentUserPalm,
  postVerifyUserPalm,
  type UserIdentifyResponse,
} from "../api/apis";
import { getPayloadWithSession, submitResponse } from "../api/sessionClient";
import {
  getIdpInternalNullifier,
  login,
  removeUser,
  updateUserIdentify,
} from "../store/user";
import type { PalmCaptureResult, PalmModule } from "../types/palm";
import { PALM_LEFT, PALM_RIGHT } from "./constants";
import {
  decryptPayload,
  encodeBase64,
  encryptPayload,
  importKeyFromArrayBuffer,
  importKeyFromBase64,
  uint8ArrayToBase64,
} from "./cryptoUtils";
import { generateProof } from "./galxeIdentityProtocol";
import { isDemoVersion } from "./utils";

export enum ReviewStatus {
  Rejected = "rejected",
  Pending = "pending",
  Approved = "approved",
}

export const loadLastPalmSide = (): number => {
  return localStorage.getItem("lastPalmSide") === "0" ? PALM_LEFT : PALM_RIGHT;
};
export const saveLastPalmSide = (side: number) =>
  localStorage.setItem("lastPalmSide", side === PALM_LEFT ? "0" : "1");

export const sideListFromAction = (action: IScanAction) => {
  const isDemo = isDemoVersion();

  if (isDemo) {
    return [PALM_RIGHT];
  }

  if (action === "create") {
    return [PALM_RIGHT, PALM_RIGHT, PALM_LEFT, PALM_LEFT];
  } else if (action === "login") {
    return [PALM_RIGHT, PALM_LEFT];
  }
  return [loadLastPalmSide()];
};

export const getPalm64FromResult = (
  Module: PalmModule,
  captureResult: PalmCaptureResult,
): string => {
  const resultPtr = Module.getPalmCaptureResultDataPtr(captureResult);
  const resultBytes = new Uint8Array(
    Module.HEAPU8.buffer,
    resultPtr,
    captureResult.data_size,
  );

  const base64String = uint8ArrayToBase64(resultBytes);

  Module.PalmCaptureResult_Free(captureResult);

  return base64String;
};

export const userSignin = async (palmList: string[], nonce: string) => {
  const res = await postUserSignin({
    palms: palmList.map((palmModel) => ({
      palmModel,
      nonce,
    })),
  });
  login(res);
};

export const createUser = async (
  palmList: string[],
  nonce: string,
  options?: {
    isUpdatePalms: boolean;
  },
) => {
  const isDemo = isDemoVersion();
  const { isUpdatePalms } = options || {};

  const palms = [];
  let palm1 = {
    palmModel: palmList[1],
    verifyPalmModels: [palmList[0]],
    nonce,
  };

  if (isDemo) {
    palm1 = {
      palmModel: palmList[0],
      verifyPalmModels: [],
      nonce,
    };
  }
  palms.push(palm1);
  if (palmList.length > 3) {
    palms.push({
      palmModel: palmList[3],
      verifyPalmModels: [palmList[2]],
      nonce,
    });
  }

  try {
    if (isUpdatePalms) {
      const urlParams = new URLSearchParams(window.location.search);

      const clientId =
        urlParams.get("clientId") || urlParams.get("client_id") || "";
      const externalUserId =
        urlParams.get("userId") || urlParams.get("user_id") || "";

      if (externalUserId && clientId) {
        const res = await postReregisterUserPalms({
          palms,
          externalUserId,
          clientId,
        });
        login(res as unknown as UserIdentifyResponse);
      } else {
        const res = await postMeRegisterPalms({
          palms,
        });
        if ((res as unknown as { message: string }).message) {
          throw res;
        }
        updateUserIdentify(res);
      }

      return;
    }

    const res = await postCreateUser({
      name: Date.now().toString(),
      palms,
    });
    login(res as UserIdentifyResponse);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);

    if (message.includes("Duplicate model found")) {
      await userSignin(palmList, nonce);
      return;
    }
    console.error("Scan Palm error:", error);
    console.error("Scan Palm error:", message);
    throw error;
  }
};

export type IScanAction =
  | "create"
  | "login"
  | "verify"
  | "oauth"
  | "verify-skip"
  | "update-palms"
  | "email-login";

export const handleScanAction = async (
  action: IScanAction,
  params: {
    palmList: string[];
    nonce: string;
    identityCommitment: string;
    userReviewStatus: string;
  },
) => {
  const urlParams = new URLSearchParams(window.location.search);
  const sessionId = urlParams.get("sessionId") || urlParams.get("session_id");
  const clientId = urlParams.get("clientId") || urlParams.get("client_id");
  const key = urlParams.get("key");
  const requestId = urlParams.get("requestId") || urlParams.get("request_id");
  const externalUserId = urlParams.get("userId") || urlParams.get("user_id");
  const cb = urlParams.get("cb");
  const experience = urlParams.get("experience");

  const fullParams: IUrlParams = {
    sessionId,
    clientId,
    key,
    requestId,
    externalUserId,
    cb,
    experience,
    ...params,
  };

  if (action === "create") {
    await createUser(params.palmList, params.nonce);
  } else if (action === "login") {
    await userSignin(params.palmList, params.nonce);
  } else if (action === "verify") {
    await verifyZK(fullParams);
  } else if (action === "verify-skip") {
    await VerifySkip(fullParams);
  } else if (action === "oauth") {
    await oauthFlow(fullParams);
  } else if (action === "email-login") {
    await emailLogin(params.palmList, params.nonce);
  }
};

interface IUrlParams {
  sessionId: string | null;
  requestId: string | null;
  clientId: string | null;
  key: string | null;
  externalUserId: string | null;
  cb: string | null; // if cb is not null, will redirect to connect.very.org
  experience: string | null;

  palmList: string[];
  nonce: string;
  identityCommitment: string;
}

const VerifySkip = async (params: IUrlParams) => {
  const { palmList, nonce, identityCommitment } = params;
  const palmModel = palmList[0];
  await postVerifyCurrentUserPalm({
    mode: null,
    palmModel,
    nonce,
    clientId: null,
    oauthAuthorizationRequestId: null,
    externalUserId: null,
    identityCommitment,
  });
  return;
};

const verifyZK = async (params: IUrlParams) => {
  const { sessionId, key, palmList, nonce, identityCommitment } = params;

  if (!sessionId || !key) {
    throw new Error("Session ID or key not found");
  }

  const palmModel = palmList[0];

  const { signedCredential } = await postVerifyCurrentUserPalm({
    mode: "anonymous",
    palmModel,
    nonce,
    clientId: null,
    oauthAuthorizationRequestId: null,
    externalUserId: null,
    identityCommitment,
  });
  const payloadResponse = await getPayloadWithSession(sessionId);
  const keyObj = await importKeyFromBase64(key);
  console.log("payloadResponse", keyObj, payloadResponse);
  const payload = (await decryptPayload(
    keyObj,
    payloadResponse.iv,
    payloadResponse.payload,
  )) as string;

  const idpQuery = JSON.parse(payload).idpQuery;

  console.log("payload", payload);

  const idpInternalNullifier = getIdpInternalNullifier();
  console.log("proof params", {
    idpInternalNullifier,
    signedCredential,
    idpQuery,
  });
  if (!idpInternalNullifier) {
    throw new Error("IdpInternal Nullifier not found");
  }
  if (!signedCredential) {
    throw new Error("Signed credential not found");
  }
  const proof = await generateProof(
    idpInternalNullifier,
    signedCredential,
    idpQuery,
  );
  console.log("proof", proof);

  const iv = crypto.getRandomValues(new Uint8Array(12)); // 96-bit IV for AES-GCM
  const encryptedProof = await encryptPayload(keyObj, iv, proof);
  const ivBase64 = encodeBase64(iv.buffer);

  await submitResponse(sessionId, ivBase64, encodeBase64(encryptedProof));
};

const oauthFlow = async (params: IUrlParams) => {
  const {
    sessionId,
    key,
    palmList,
    nonce,
    identityCommitment,
    clientId,
    requestId,
    externalUserId,
    cb,
    experience,
  } = params;
  if (!clientId || !requestId) {
    throw new Error("Client ID or request ID not found");
  }
  const palmModel = palmList[0];
  let res = {
    authCode: "success",
  } as {
    authCode: string | null;
    signedCredential?: string | null;
  };
  if (externalUserId && externalUserId !== "null") {
    res = await postVerifyUserPalm({
      palmModel,
      nonce,
      clientId: clientId,
      externalUserId: externalUserId,
      oauthAuthorizationRequestId: requestId,
    });
  } else {
    res = await postVerifyCurrentUserPalm({
      mode: "pseudonymous",
      palmModel,
      nonce,
      clientId: clientId,
      oauthAuthorizationRequestId: requestId,
      externalUserId: null,
      identityCommitment,
    });
  }
  if (!res) {
    throw new Error("Failed to verify palm");
  }

  const { authCode, signedCredential } = res;

  if (cb) {
    if (experience === "web_only") {
      removeUser();
    }
    window.location.href = `/verify-go-back?code=${authCode}&auth_request_id=${requestId}`;
    return;
  }

  if (!sessionId || !key) {
    throw new Error("Session ID or key not found");
  }

  const jsonData = {
    authCode,
    signedCredential,
  };

  const keyArray = new Uint8Array(
    key.match(/.{1,2}/g)?.map((byte) => parseInt(byte, 16)) || [],
  );

  const keyObj = await importKeyFromArrayBuffer(keyArray.buffer);

  const iv = crypto.getRandomValues(new Uint8Array(12)); // 96-bit IV for AES-GCM
  const encryptedProof = await encryptPayload(keyObj, iv, jsonData);
  const ivBase64 = encodeBase64(iv.buffer);

  await submitResponse(sessionId, ivBase64, encodeBase64(encryptedProof));

  return;
};

export const emailLogin = async (palmList: string[], nonce: string) => {
  const urlParams = new URLSearchParams(window.location.search);
  const email = urlParams.get("email");
  if (!email) {
    throw new Error("Email not found");
  }
  const res = await postUserAuthenticate({
    email: email,
    palmModel: palmList[0],
    nonce,
  });
  login(res as UserIdentifyResponse);
};
